import { uiLocaleTag, type UiLocale } from "@/shared/lib/ui-locale";

/**
 * Единственная низкоуровневая точка форматирования даты и времени.
 *
 * Пояс фиксирован (МСК): турнир начинается в одно и то же время для всех,
 * и дата матча не должна разъезжаться у пользователя из другого пояса.
 * Локаль по умолчанию — локаль интерфейса, а не языка браузера (KAN-997).
 */
export const MSK_TIME_ZONE = "Europe/Moscow";

export function parseUtcIsoToMs(iso?: string | null): number | null {
  if (!iso) return null;
  const s = String(iso).trim();
  if (!s) return null;
  const hasTz = /([zZ]|[+-]\d\d:\d\d)$/.test(s);
  const d = new Date(hasTz ? s : `${s}Z`);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function toMskPickerDateFromUtcIso(
  iso?: string | null,
): Date | undefined {
  if (!iso) return undefined;
  const utc = new Date(iso);
  if (Number.isNaN(utc.getTime())) return undefined;

  const localOffset = utc.getTimezoneOffset();
  const shiftedUtcMs = utc.getTime() + (180 + localOffset) * 60_000;
  return new Date(shiftedUtcMs);
}

export function fromMskPickerDateToUtcIso(
  date?: Date | null,
): string | undefined {
  if (!date) return undefined;
  const localOffset = date.getTimezoneOffset();
  const utcMs = date.getTime() - (180 + localOffset) * 60_000;
  return new Date(utcMs).toISOString();
}

/** На входе бывает и ISO-строка, и готовый Date, и миллисекунды. */
export type DateLike = string | number | Date;

function toDate(value: DateLike): Date {
  return value instanceof Date ? value : new Date(value);
}

export function formatDateMsk(
  value: DateLike,
  options?: Intl.DateTimeFormatOptions,
  locale?: UiLocale,
): string {
  return toDate(value).toLocaleString(uiLocaleTag(locale), {
    timeZone: MSK_TIME_ZONE,
    ...(options ?? {}),
  });
}

export function formatDateOnlyMsk(
  value: DateLike,
  options?: Intl.DateTimeFormatOptions,
  locale?: UiLocale,
): string {
  return toDate(value).toLocaleDateString(uiLocaleTag(locale), {
    timeZone: MSK_TIME_ZONE,
    ...(options ?? {}),
  });
}

export function formatTimeOnlyMsk(
  value: DateLike,
  options?: Intl.DateTimeFormatOptions,
  locale?: UiLocale,
): string {
  return toDate(value).toLocaleTimeString(uiLocaleTag(locale), {
    timeZone: MSK_TIME_ZONE,
    ...(options ?? {}),
  });
}
